import { Injectable, OnDestroy } from '@angular/core';
import { QuestionAnswerGetResponse } from '../api/models/question-answer-get-response';
import { SurveyService, QuestionAnswerService, FilesService } from '../api/services';
import { forkJoin, BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { SurveyGetResponse } from '../api/models/survey-get-response';
import { ProgressSpinnerService } from './progress-spinner.service';
import { SurveySectionGetResponse } from '../api/models/survey-section-get-response';
import {
  GetFilledResponsesPercentageResponse,
  LanguageEnum,
  QuestionAnswerPostResponse,
  QuestionGroupResponse,
  SurveyInstanceDataResponse,
  UploadedFileResponse,
  QuestionResponse,
} from '../api/models';
import { SelectedOptionRequest } from '../api/models/selected-option-request';
import { SurveyInstanceDataService } from '../api/services/survey-instance-data.service';
import { mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LanguageService } from './language.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from './account.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { FOYData } from './foy-data';
import { FOYDataService } from './foy-data.service';
import { PendingChangesService } from './pending-changes.service';

@Injectable({
  providedIn: 'root',
})
export class SurveyStateService implements OnDestroy {
  private surveyInstanceId: number;
  private Survey: SurveyGetResponse;
  private QuestionGroupsInOrder: QuestionGroupResponse[] = [];
  private CurrentQuestionGroup: QuestionGroupResponse;
  private CurrentSection: SurveySectionGetResponse;
  private questionAnswersForTheCurrentQuestionGroup: QuestionAnswerGetResponse;
  private AllQuestionAnswersInstanceOptionIdSelectedList = [];
  private surveyInitialisedSubject = new BehaviorSubject<boolean>(null);
  private questionGroupLoadedSubject = new BehaviorSubject<boolean>(null);
  private answerSavedSubject = new Subject<QuestionAnswerPostResponse>();
  private responsesPercentagesChangedSubject = new Subject<GetFilledResponsesPercentageResponse>();
  private getFilledResponsesPercentageResponse: GetFilledResponsesPercentageResponse;
  public surveyInitialised = this.surveyInitialisedSubject.asObservable();
  public questionGroupLoaded = this.questionGroupLoadedSubject.asObservable();
  public answerSaved = this.answerSavedSubject.asObservable();
  public responsesPercentagesChanged = this.responsesPercentagesChangedSubject.asObservable();
  public isSaving = false;
  public isForwardDirection = true;

  private lastObservedSuppliersCountSubject = new BehaviorSubject<number>(Number.MAX_VALUE);
  public lastObservedSuppliersCountObservable = this.lastObservedSuppliersCountSubject.asObservable();
  public lastObservedSuppliersCountValue = Number.MAX_VALUE;

  private lastObservedSuppliersSpendSubject = new BehaviorSubject<number>(Number.MAX_VALUE);
  public lastObservedSuppliersSpendObservable = this.lastObservedSuppliersSpendSubject.asObservable();
  public lastObservedSuppliersSpendValue = Number.MAX_VALUE;

  ngUnsubscribe = new Subject();

  constructor(
    private surveyService: SurveyService,
    private accountService: AccountService,
    private questionAnswerService: QuestionAnswerService,
    private progressSpinnerService: ProgressSpinnerService,
    private surveyInstanceDataService: SurveyInstanceDataService,
    private languageService: LanguageService,
    private filesService: FilesService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private translate: TranslateService,
    private foyDataService: FOYDataService,
    private pendingChangesService: PendingChangesService,
  ) {
    combineLatest([
      this.languageService.languageChanged$.asObservable(),
      this.accountService.userSubject$.asObservable(),
      this.accountService.selectedSurveyInstanceId$.asObservable(),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([lang, user, surveyInstanceId]) => {
        if (surveyInstanceId && lang) {
          this.initSurveyCommand2(surveyInstanceId, lang).subscribe();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  initSurveyCommand2(surveyInstanceId: number, language: LanguageEnum): Observable<any> {
    this.surveyInstanceId = surveyInstanceId;
    var currentQuestionGroup = +this.route.snapshot.queryParams['currentQuestionGroup'];
    this.progressSpinnerService.show();
    const tasks$ = [];
    tasks$.push(this.surveyService.apiSurveyGet$Json({ language, surveyInstanceId }));
    tasks$.push(this.questionAnswerService.apiQuestionAnswerGetFilledResponsesPercentageGet$Json({ surveyInstanceId }));

    return forkJoin(tasks$).pipe(
      tap(
        (responses: any[]) => {
          this.progressSpinnerService.hide();
          this.Survey = responses[0];
          this.AllQuestionAnswersInstanceOptionIdSelectedList =
            responses[0].allQuestionAnswersInstanceOptionIdSelectedList;
          this.CreateOrderedQuestionGroupList();

          if (currentQuestionGroup) {
            this.CurrentQuestionGroup = this.QuestionGroupsInOrder.filter((a) => a.id === currentQuestionGroup)[0];
          } else {
            this.CurrentQuestionGroup = this.QuestionGroupsInOrder[0];
          }
          this.getFilledResponsesPercentageResponse = responses[1];
          this.responsesPercentagesChangedSubject.next(this.getFilledResponsesPercentageResponse);

          this.loadQuestionsForTheCurrentQuestionGroupCommand();
          this.surveyInitialisedSubject.next(true);
        },
        (_) => {
          this.progressSpinnerService.hide();
          this.surveyInitialisedSubject.next(false);
        },
      ),
    );
  }

  loadQuestionsForTheCurrentQuestionGroupCommand() {
    this.progressSpinnerService.show();
    if (!this.CurrentQuestionGroup) {
      console.error('CurrentQuestionGroup is not set');
    }
    this.questionAnswerService
      .apiQuestionAnswerGet$Json({
        questionGroupId: this.CurrentQuestionGroup.id,
        language: this.languageService.language,
        surveyInstanceId: this.surveyInstanceId,
      })
      .subscribe(
        (data) => {
          this.questionAnswersForTheCurrentQuestionGroup = data;
          this.SetCurrentSection(data.questionListInGroup[0].surveySectionId);
          this.questionGroupLoadedSubject.next(true);
          this.progressSpinnerService.hide();
          this.GoToNextQuestionGroupIfNoQuestionsVisible();
        },
        (_) => {
          this.progressSpinnerService.hide();
          this.questionGroupLoadedSubject.next(false);
        },
      );
  }

  saveQuestionAnswersCommand(selectedAnswerOptionIdList: SelectedOptionRequest[], questionId) {
    this.isSaving = true;
    this.questionAnswerService
      .apiQuestionAnswerPost$Json({
        body: {
          selectedAnswerOptionIdList,
          questionId,
        },
        surveyInstanceId: this.surveyInstanceId,
      })
      .pipe(
        tap((_) => {
          var isIndustryQuestion = this.foyDataService.isIndustryQuestion(questionId);
          var isCurrencyQuestion = this.foyDataService.isCurrencyQuestion(questionId);
          if (isCurrencyQuestion || isIndustryQuestion) {
            this.foyDataService.updateCurrency().subscribe(() => {
                // After updating surveyType, reload questions in the current group
              this.loadQuestionsForTheCurrentQuestionGroupCommand();
            });
          }
        }),
        mergeMap((data) => {
          if (data.successStatus) {
            this.messageService.add({
              severity: 'info',
              summary: this.translate.instant('Questionnaire updated'),
              detail: this.translate.instant('Inputs saved successfully'),
              life: 5000,
              closable: true,
            });
            this.AllQuestionAnswersInstanceOptionIdSelectedList = data.allQuestionAnswersInstanceOptionIdSelectedList;
          }

          this.answerSavedSubject.next(data);
          this.isSaving = false;
          return this.questionAnswerService.apiQuestionAnswerGetFilledResponsesPercentageGet$Json({
            surveyInstanceId: this.surveyInstanceId,
          });
        }),
      )
      .subscribe(
        (data) => {
          this.getFilledResponsesPercentageResponse = data;
          this.responsesPercentagesChangedSubject.next(data);
          this.pendingChangesService.setHasPendingChanges(false);
        },
        (_) => {
          this.answerSavedSubject.next({ successStatus: false, questionId });
          this.isSaving = true;
          this.pendingChangesService.setHasPendingChanges(false);
        },
      );
  }

  updateFilledResponsesPercentage() {
    this.questionAnswerService.apiQuestionAnswerGetFilledResponsesPercentageGet$Json({
      surveyInstanceId: this.surveyInstanceId,
    })
    .subscribe((data) => {
      this.getFilledResponsesPercentageResponse = data;
      this.responsesPercentagesChangedSubject.next(data);
      this.pendingChangesService.setHasPendingChanges(false);
    });
  }

  deleteQuestionAnswersCommand(questionId) {
    this.saveQuestionAnswersCommand([], questionId);
  }

  checkVisibilityCondition(
    isComponentVisible: boolean,
    dependsOnAnswerOptionIdCollection: number[],
    questionId: number,
  ): boolean {
    if (dependsOnAnswerOptionIdCollection.length > 0) {
      const shouldBeVisible =
        this.AllQuestionAnswersInstanceOptionIdSelectedList.filter((a) => dependsOnAnswerOptionIdCollection.includes(a))
          .length > 0;
      if (shouldBeVisible === false && isComponentVisible === true) {
        this.deleteQuestionAnswersCommand(questionId);
      }
      return shouldBeVisible;
    } else {
      return true;
    }
  }

  private CreateOrderedQuestionGroupList() {
    this.QuestionGroupsInOrder = [];
    this.Survey.surveySectionGetResponseList.forEach((section) => {
      this.QuestionGroupsInOrder = this.QuestionGroupsInOrder.concat(section.questionGroupList);
    });
    this.QuestionGroupsInOrder.sort((n1, n2) => n1.orderNumber - n2.orderNumber);
  }

  private SetCurrentSection(sectionId) {
    this.CurrentSection = this.GetSurveySections().filter((a) => a.id === sectionId)[0];
  }

  public GetSurveySections() {
    return this.Survey.surveySectionGetResponseList.sort((a, b) => a.orderNumber - b.orderNumber);
  }

  public GetActiveSurveySection() {
    return this.CurrentSection;
  }

  public GetCurrentQuestionGroup() {
    return this.CurrentQuestionGroup;
  }

  public GetCurrentQuestionAnswerInstanceListInGroup() {
    return this.questionAnswersForTheCurrentQuestionGroup.questionAnswerInstanceListInGroup;
  }

  public GetQuestionsForTheCurrentQuestionGroupAndForSurveyType(): QuestionResponse[] {
    return this.GetQuestionsForSurveyType(this.questionAnswersForTheCurrentQuestionGroup.questionListInGroup);
  }

  public GetQuestionsForSurveyType(questions: QuestionResponse[]): QuestionResponse[] {
    const surveyType = this.foyDataService.data.surveyType;

    if (!questions) {
      return [];
    }

    // If surveyType is null, return only questions where surveyType is null
    if (surveyType === null || surveyType === undefined) {
      return questions.filter((q) => q.surveyType == null);
    }

    // Filter questions: take those where surveyType is null OR matches current surveyType
    const relevantQuestions = questions.filter(
      (q) => q.surveyType === null || q.surveyType === undefined || q.surveyType === surveyType,
    );

    // Create a map to handle duplicates by customId
    const questionsMap = new Map<string, QuestionResponse>();

    relevantQuestions.forEach((q) => {
      if (q.customId) {
        const existing = questionsMap.get(q.customId);
        // If this customId already exists, prefer the one with matching surveyType
        if (!existing || (q.surveyType === surveyType && existing.surveyType !== surveyType)) {
          questionsMap.set(q.customId, q);
        }
      } else {
        // Questions without customId are added with a unique key based on their id
        questionsMap.set(`id_${q.id}`, q);
      }
    });

    // Return values from the map, sorted by orderNumber
    return Array.from(questionsMap.values()).sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  }

  public GetProgress() {
    this.getFilledResponsesPercentageResponse.overallPercentage;
    if (this.Survey) {
      return Math.round(this.getFilledResponsesPercentageResponse.overallPercentage * 100);
    }
    return 0;
  }

  public GoToNextQuestionGroup() {
    this.isForwardDirection = true;
    const index = this.QuestionGroupsInOrder.findIndex((a) => a.id === this.CurrentQuestionGroup.id);
    if (!this.IsLastQuestionGroupVisible()) {
      this.CurrentQuestionGroup = this.QuestionGroupsInOrder[index + 1]; //
      this.loadQuestionsForTheCurrentQuestionGroupCommand();
    }
    this.changeUrl();
  }

  public GoToLastQuestionGroup() {
    this.CurrentQuestionGroup = this.QuestionGroupsInOrder[this.QuestionGroupsInOrder.length - 1];
    this.loadQuestionsForTheCurrentQuestionGroupCommand();
    var user = this.accountService.getUser();
    var selectedSurvey = user.surveys?.find((a) => a.surveyInstanceId === this.surveyInstanceId);
    if (!selectedSurvey) {
      return;
    }
    this.router.navigate([`./surveys/${selectedSurvey.surveyToken}`], {
      queryParams: { currentQuestionGroup: this.CurrentQuestionGroup.id },
      queryParamsHandling: 'merge',
    });
  }

  public GoToSummary() {
    var user = this.accountService.getUser();
    var selectedSurvey = user.surveys?.find((a) => a.surveyInstanceId === this.surveyInstanceId);
    if (!selectedSurvey) {
      return;
    }
    this.router.navigate([`/summary/${selectedSurvey.surveyToken}`]);
  }

  public GoToQuestionGroup(id: number) {
    this.isForwardDirection = true;
    const index = this.QuestionGroupsInOrder.findIndex((a) => a.id === id);
    this.CurrentQuestionGroup = this.QuestionGroupsInOrder[index]; //
    this.loadQuestionsForTheCurrentQuestionGroupCommand();
    this.changeUrl();
  }

  public IsFirstQuestionGroupVisible() {
    return this.QuestionGroupsInOrder.findIndex((a) => a.id === this.CurrentQuestionGroup.id) === 0;
  }

  public IsLastQuestionGroupVisible() {
    return (
      this.QuestionGroupsInOrder.findIndex((a) => a.id === this.CurrentQuestionGroup.id) ===
      this.QuestionGroupsInOrder.length - 1
    );
  }

  public GoToPreviousQuestionGroup() {
    this.isForwardDirection = false;
    const index = this.QuestionGroupsInOrder.findIndex((a) => a.id === this.CurrentQuestionGroup.id);
    if (index > 0) {
      this.CurrentQuestionGroup = this.QuestionGroupsInOrder[index - 1];
      this.loadQuestionsForTheCurrentQuestionGroupCommand();
    }
    this.changeUrl();
  }

  public GoToNextQuestionGroupIfNoQuestionsVisible() {
    if (this.GroupHasOnlyHiddenQuestions()) {
      if (this.isForwardDirection) {
        this.GoToNextQuestionGroup();
      } else {
        this.GoToPreviousQuestionGroup();
      }
    }
  }

  public GroupHasOnlyHiddenQuestions() {
    let visibleComponentsCount = 0;
    this.GetQuestionsForTheCurrentQuestionGroupAndForSurveyType().filter((q) => {
      if (q.dependsOnAnswerOptionIdCollection.length > 0) {
        if (
          this.AllQuestionAnswersInstanceOptionIdSelectedList.filter((b) =>
            q.dependsOnAnswerOptionIdCollection.includes(b),
          ).length > 0
        ) {
          visibleComponentsCount++;
        }
      } else {
        visibleComponentsCount++;
      }
    });
    return visibleComponentsCount === 0;
  }

  public ChangeSuppliersCount(newSuppliersCountValue) {
    this.lastObservedSuppliersCountSubject.next(newSuppliersCountValue);
  }

  public ChangeSuppliersSpend(newSuppliersSpendValue) {
    this.lastObservedSuppliersSpendSubject.next(newSuppliersSpendValue);
  }

  public uploadFiles(
    files: File[],
    answerOptionId: number,
    freeText: string,
    questionId: number,
    successCallback: (uploadedFiles: UploadedFileResponse[]) => void,
  ) {
    this.isSaving = true;
    this.filesService
      .apiFilesPost$Json({
        AnswerOptionId: answerOptionId,
        body: {
          Files: files,
        },
        FreeText: freeText,
        QuestionId: questionId,
        surveyInstanceId: this.surveyInstanceId,
      })
      .subscribe(
        (data) => {
          this.isSaving = false;
          successCallback(data);
        },
        (_) => {
          this.answerSavedSubject.next({ successStatus: false });
          this.isSaving = true;
        },
      );
  }

  public getQuestionGroupsInOrder() {
    return this.QuestionGroupsInOrder;
  }

  submitSurveyCommand() {
    this.isSaving = true;
    this.surveyService
      .apiSurveySubmitSurveyPut$Json({
        surveyInstanceId: this.surveyInstanceId,
      })
      .subscribe(
        (isSuccessStatus) => {
          if (isSuccessStatus) {
            this.messageService.add({
              severity: 'info',
              summary: 'Info',
              detail: this.translate.instant('Questionnaire successfully submitted'),
              life: 5000,
              closable: true,
            });

            var user = this.accountService.getUser();
            var selectedSurvey = user.surveys?.find((a) => a.surveyInstanceId === this.surveyInstanceId);
            if (!selectedSurvey) {
              return;
            }
            this.router.navigate([`/end-of-survey/${selectedSurvey.surveyToken}`]);
            this.accountService.markSurveyAsSubmitted(this.Survey.surveyId);
          }
          this.isSaving = false;
        },
        (_) => {
          this.answerSavedSubject.next({ successStatus: false });
          this.isSaving = true;
        },
      );
  }

  getFilledResponsesPercentage(): GetFilledResponsesPercentageResponse {
    return this.getFilledResponsesPercentageResponse;
  }

  private changeUrl() {
    this.router.navigate([], {
      queryParams: { currentQuestionGroup: this.CurrentQuestionGroup.id },
      queryParamsHandling: 'merge',
    });
  }

  public isOldSurvey(oldSurveyId: number): boolean {
    return this.Survey.surveyId == oldSurveyId;
  }
}
